import type { CSSProperties } from "react";
import Color from "colorjs.io";
import type { WidgetBackgroundStyle } from "@/types/ui/branding";

/**
 * True when light text reads better than dark text on this background — its
 * colour (both stops of a gradient, the base of a pattern) sits below the
 * ~0.18 relative luminance where white starts to out-contrast black. Unset or
 * unparseable backgrounds count as light, the default page.
 */
export function isDarkBackground(bg: WidgetBackgroundStyle | null | undefined): boolean {
  if (!bg?.color) return false;
  const stops = bg.type === "gradient" && bg.color2 ? [bg.color, bg.color2] : [bg.color];
  try {
    const luminance = stops.reduce((sum, stop) => sum + new Color(stop).luminance, 0) / stops.length;
    return luminance < 0.18;
  } catch {
    return false;
  }
}

/**
 * Resolves a widget popup background config into inline CSS. Pattern presets
 * are a small, curated set of gradient-based tricks (not freeform CSS), so
 * clients can only ever produce one of these known-safe looks.
 */
export function widgetBackgroundToCss(
  bg: WidgetBackgroundStyle | null | undefined,
  fallback: string,
): CSSProperties {
  if (!bg) return { backgroundColor: fallback };

  switch (bg.type) {
    case "solid":
      return { backgroundColor: bg.color };

    case "gradient":
      return {
        backgroundImage: `linear-gradient(${bg.angle ?? 135}deg, ${bg.color}, ${bg.color2 ?? bg.color})`,
      };

    case "pattern": {
      const accent = bg.color2 ?? "rgba(0,0,0,0.06)";
      switch (bg.patternId) {
        case "grid": {
          const size = bg.patternSize ?? 20;
          return {
            backgroundColor: bg.color,
            backgroundImage: `linear-gradient(${accent} 1px, transparent 1px), linear-gradient(90deg, ${accent} 1px, transparent 1px)`,
            backgroundSize: `${size}px ${size}px`,
          };
        }
        case "diagonal-lines": {
          const pitch = bg.patternSize ?? 12;
          return {
            backgroundColor: bg.color,
            backgroundImage: `repeating-linear-gradient(45deg, ${accent} 0, ${accent} 1px, transparent 1px, transparent ${pitch}px)`,
          };
        }
        case "dots":
        default: {
          const size = bg.patternSize ?? 14;
          return {
            backgroundColor: bg.color,
            backgroundImage: `radial-gradient(${accent} 1px, transparent 1px)`,
            backgroundSize: `${size}px ${size}px`,
          };
        }
      }
    }

    default:
      return { backgroundColor: fallback };
  }
}
